import { useRef, useEffect } from 'react';
import * as d3 from 'd3';

// Core D3 integration hook
export const useD3 = (renderChartFn, dependencies) => {
  const ref = useRef();

  useEffect(() => {
    if (ref.current && dependencies.every(dep => dep !== undefined)) {
      const svg = d3.select(ref.current);
      
      // Clear previous content
      svg.selectAll("*").remove();
      
      // Render chart
      renderChartFn(svg);
    }

    // Cleanup function
    return () => {
      if (ref.current) {
        const svg = d3.select(ref.current);
        svg.selectAll("*").remove();
        svg.on('.zoom', null); // Remove zoom listeners
      }
    };
  }, dependencies);

  return ref;
};

// Responsive D3 hook
export const useResponsiveD3 = (renderChartFn, dependencies, aspectRatio = 2) => {
  const containerRef = useRef();
  const svgRef = useRef();

  useEffect(() => {
    if (!containerRef.current || !svgRef.current) return;

    const handleResize = () => {
      const containerWidth = containerRef.current.clientWidth;
      const width = containerWidth;
      const height = width / aspectRatio;

      const svg = d3.select(svgRef.current);
      svg.attr('width', width).attr('height', height);

      // Clear and re-render
      svg.selectAll("*").remove();
      renderChartFn(svg, { width, height });
    };

    // Initial render
    handleResize();

    // Add resize listener
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
      }
    };
  }, dependencies);

  return { containerRef, svgRef };
};

// D3 tooltip hook
export const useD3Tooltip = () => {
  const tooltipRef = useRef();

  useEffect(() => {
    // Create tooltip div if it doesn't exist
    if (!tooltipRef.current) {
      tooltipRef.current = d3
        .select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '8px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');
    }

    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, []);

  const showTooltip = (content, event) => {
    if (tooltipRef.current) {
      tooltipRef.current
        .style('visibility', 'visible')
        .html(content)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
    }
  };

  const hideTooltip = () => {
    if (tooltipRef.current) {
      tooltipRef.current.style('visibility', 'hidden');
    }
  };

  const moveTooltip = (event) => {
    if (tooltipRef.current) {
      tooltipRef.current
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
    }
  };

  return { showTooltip, hideTooltip, moveTooltip };
};